<template>
  <GridPage
    title="Orders"
    subtitle=""
    viewset="/shop/admin/orders/"
    :actions="actions"
    :filters="filters"
    :fields="fields"
    :showAddButton="false"
    :showExportButton="true"
    :refresh="refresh"
    :ready="ready === 0"
    @addButton="handleAddButton"
    @rowAction="handleRowAction"
    @exportButton="handleExportAllClick"
    @query="handleUpdateQuery"
  >
    <template v-slot:modals>
      <AddModal
        :show-modal="showAddModal"
        @close="handleCloseAddModal"
        @dataUpdated="handleDataUpdated"
      />
      <TransactionsModal
        :orderKey="transactionsOrderKey"
        @close="handleTransactionModalClose"
      />
      <ContactModal
        :orderDetails="contactOrderDetails"
        @close="handleContactModalClose"
      />
      <UpdateStatusModal
        :orderKey="updateStatusOrderKey"
        @close="handleUpdateStatusModalClose"
      />
      <ReceiptModal
        :orderKey="receiptOrderKey"
        @close="handleReceiptModalClose"
      />
      <PickupDatetimeModal
        :pk="pickupDatetimePK"
        @close="handlePickupDatetimeModalClose"
      />
      <VoidModal :orderKey="voidOrderKey" @close="handleVoidModalClose" />
    </template>

    <template v-slot:filters>
      <DropDownCheckList
        :items="paymentStatusOptions"
        text="Payment Status"
        icon="filter_list"
        v-model="paymentStatusFilter"
        highlightClass="bg-light"
        class="mx-1"
        radio
      />
      <DropDownCheckList
        :items="orderStatusOptions"
        text="Order Status"
        icon="filter_list"
        v-model="orderStatusFilter"
        highlightClass="bg-light"
        class="mx-1"
        radio
      />
    </template>
  </GridPage>
</template>

<script>
import GridPage from "@/components/layout/Page/GridPage.vue";
import DropDownCheckList from "@/components/DropDownChecklist.vue";

import AddModal from "../Products/AddModal";
import TransactionsModal from "./TransactionsModal";
import ContactModal from "./ContactModal";
import UpdateStatusModal from "./UpdateStatusModal";
import VoidModal from "./VoidModal";
import ReceiptModal from "./ReceiptModal";
import PickupDatetimeModal from "./PickupDatetimeModal";

import formatters from "@/components/DataGrid/formatters";

export default {
  name: "odd-view-orders-main",
  data() {
    return {
      showAddModal: false,
      transactionsOrderKey: null,
      pickupDatetimePK: null,
      contactOrderDetails: null,
      voidOrderKey: null,
      receiptOrderKey: null,
      editPK: null,
      updateStatusOrderKey: null,
      paymentStatusOptions: [
        { name: "", title: "All" },
        { name: "v", title: "Void" },
        { name: "p", title: "Paid" },
        { name: "u", title: "Unpaid" }
      ],
      paymentStatusFilter: "",
      orderStatusFilter: "",
      refresh: 0,
      ready: 1,
      actions: [
        {
          name: "Transactions",
          icon: "payment",
          event: "transactions"
        },
        {
          name: "Customer",
          icon: "person",
          event: "contact"
        },
        {
          name: "Receipt",
          icon: "receipt",
          event: "receipt"
        },
        {
          name: "Pickup Time",
          icon: "schedule",
          event: "pickup_time",
        },
        {
          name: "Update Status",
          icon: "timeline",
          event: "update_status",
          condition(rowData) {
            return rowData.order_status !== "VOID";
          }
        },
        {
          name: "Void Order",
          icon: "close",
          theme: "danger",
          event: "void_order",
          condition(rowData) {
            return rowData.order_status !== "VOID";
          }
        }
      ],
      fields: [
        {
          name: "_select-slot",
          title: "Select",
          visible: false,
          optional: false
        },
        {
          name: "key",
          visible: false
        },
        {
          name: "customer",
          title: "Customer",
          sortField: "name",
          formatter: formatters.customer.complex,
          visible: true
        },
        {
          name: "_source",
          title: "Source",
          sortField: "_source",
          visible: false
        },
        {
          name: "created",
          sortField: "created",
          visible: true,
          formatter: formatters.datetime.withSubtext
        },
        {
          name: "fulfillment",
          sortField:"_SORT_FIELD_fulfillment_datetime",
          visible: true,
          formatter: formatters.fulfillment.complex,
        },
        {
          name: "order_status",
          sortField: "order_status",
          title: "Order Status",
          visible: true,
          align: "center",
          formatter: formatters.orderStatus.asBadge
        },
        {
          name: "payment_status",
          title: "Payment Status",
          sortField: '_SORT_FIELD_payment_status',
          formatter: formatters.paymentStatus.asBadge,
          align: "center"
        },
        {
          name: "phone",
          visible: false
        },
        {
          name: "user_email",
          title: "Email",
          sortField: "user_email",
          visible: false
        },
        {
          name: "label_count",
          title: "Labels Printed",
          visible: false
        },
        {
          name: "total_gross",
          title: "Total",
          visible: true,
          formatter: formatters.currency.usd,
          monospace: true,
          sortField: "total_gross",
          titleClass: "text-right",
          dataClass: "text-right text-monospace"
        },
        {
          name: "num_items",
          title: "Item Count",
          visible: true,
          titleClass: "text-right",
          dataClass: "text-right text-monospace"
        },
              {
          name: "finalized",
          title: "Finalized",
          sortField: "finalized",
          visible: true,
          formatter: formatters.boolean.checkmark,
          titleClass: "text-right",
          dataClass: "text-right text-monospace"
        }
      ],
      query: ''
    };
  },
  computed: {
    filters() {
      return {
        payment_status: this.paymentStatusFilter,
        order_status: this.orderStatusFilter
      };
    },
    orderStatusOptions() {
      const orderStatusOptionsList = [{ name: "", title: "All" }];
      this.$store.state.orderStatusOptions.list.forEach(option => {
        orderStatusOptionsList.push({
          name: option.key,
          title: option.internal_label
        });
      });
      return orderStatusOptionsList;
    }
  },
  components: {
    GridPage,
    AddModal,
    DropDownCheckList,
    TransactionsModal,
    ContactModal,
    UpdateStatusModal,
    VoidModal,
    ReceiptModal,
    PickupDatetimeModal
  },
  methods: {
    handleRowAction(data) {
      if (data.action.event === "transactions") {
        this.transactionsOrderKey = data.props.rowData.key;
      }
      if (data.action.event === "contact") {
        this.contactOrderDetails = data.props.rowData;
      }
      if (data.action.event === "receipt") {
        this.receiptOrderKey = data.props.rowData.key;
      }
      if (data.action.event === "update_status") {
        this.updateStatusOrderKey = data.props.rowData.key;
      }
      if (data.action.event === "void_order") {
        this.voidOrderKey = data.props.rowData.key;
      }
      if (data.action.event === "pickup_time") {
        this.pickupDatetimePK = data.props.rowData.key;
      }

    },
    handleCloseEditModal(event) {
      this.editPK = null;
    },
    handleAddButton(event) {
      this.showAddModal = true;
    },
    handleCloseAddModal(event) {
      this.showAddModal = false;
    },
    handleDataUpdated(event) {
      this.refresh = this.refresh + 1;
    },
    handleTransactionModalClose() {
      this.transactionsOrderKey = null;
    },
    handleContactModalClose() {
      this.contactOrderDetails = null;
    },
    handleUpdateStatusModalClose(refresh) {
      if (refresh) {
        this.handleDataUpdated();
      }
      this.updateStatusOrderKey = null;
    },
    handleReceiptModalClose(refresh) {
      if (refresh) {
        this.handleDataUpdated();
      }
      this.receiptOrderKey = null;
    },
    handleVoidModalClose(refresh) {
      if (refresh) {
        this.handleDataUpdated();
      }
      this.voidOrderKey = null;
    },
    handlePickupDatetimeModalClose(refresh) {
      if (refresh) {
        this.handleDataUpdated();
      }
      this.pickupDatetimePK = null;
    },
    handleExportAllClick(event) {
      this.$store.dispatch("orders/export", this.query).then(data => {
        console.log(data);
      });
    },
    handleUpdateQuery(query) {
      this.query = query
    }
  },
  mounted() {
    this.ready -= 1;
  },
  created() {
    this.ready = this.ready + 1;
    this.$store.dispatch("orderStatusOptions/init").then(() => {
      this.ready = this.ready - 1;
    });
  },
  activated() {
    this.handleDataUpdated();
  }
};
</script>
